'use client';

import { useForm } from 'react-hook-form';

import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { Field, Form } from '@/components/hook-form';
import { GQLMutation } from '@/lib/client';
import { USER_LOGOUT, USER_UPDATE_SELF_PHONE } from '@/lib/mutations/user.mutation';
import { useTranslate } from '@/locales/use-locales';
import { validatePhoneNumber } from '@/utils/helper';

type PhoneValues = { phone: string };

const normalizePhone = (phone: string) => phone.replace(/\D/g, '');

export function CompletePhoneView({ name }: { name?: string }) {
  const { t } = useTranslate();
  const methods = useForm<PhoneValues>({ defaultValues: { phone: '' } });
  const phone = methods.watch('phone').trim();
  const phoneIsValid = validatePhoneNumber(phone);
  const { action: savePhone, loading: saving } = GQLMutation({
    mutation: USER_UPDATE_SELF_PHONE,
    resolver: 'userUpdatePhoneSelf',
    toastmsg: true,
    callback: () => window.location.assign('/complete-profile'),
  });
  const { action: logout, loading: loggingOut } = GQLMutation({
    mutation: USER_LOGOUT,
    resolver: 'logout',
    toastmsg: false,
    callback: () => window.location.assign('/'),
  });

  const submit = methods.handleSubmit(({ phone: inputPhone }) => {
    const value = inputPhone.trim();
    methods.clearErrors();
    if (!validatePhoneNumber(value)) {
      methods.setError(
        'phone',
        { message: t('auth.validation.phoneInternational') },
        { shouldFocus: true }
      );
      return;
    }
    savePhone({ variables: { input: { phone: normalizePhone(value) } } });
  });

  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', py: 4 }}>
      <Container maxWidth="sm">
        <Card sx={{ p: { xs: 3, sm: 5 } }}>
          <Typography variant="h4" sx={{ mb: 1 }}>
            {t('auth.phoneCompletion.title')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>
            {t('auth.phoneCompletion.description', { name: name || t('auth.phoneCompletion.you') })}
          </Typography>

          <Form methods={methods} onSubmit={submit}>
            <Box display="flex" flexDirection="column" gap={3}>
              <Field.Phone
                name="phone"
                label={t('auth.register.phone')}
                country="KE"
                autoFocus
              />
              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={saving}
                disabled={!phoneIsValid}
              >
                {t('auth.phoneCompletion.submit')}
              </LoadingButton>
              <Button
                fullWidth
                color="inherit"
                disabled={loggingOut}
                onClick={() => logout()}
              >
                {t('auth.phoneCompletion.logout')}
              </Button>
            </Box>
          </Form>
        </Card>
      </Container>
    </Box>
  );
}
